"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useFormatter, useLocale, useTranslations } from "next-intl";
import { useQueryClient } from "@tanstack/react-query";
import { TourDate } from "@/types/dto/pdp/pdp-dates-inclusions-pricing";
import { DateMetadata, StaticContent } from "@/types/dto/pdp/pdp-contentful";
import formatListOfDates from "@/utils/date-utils/format-list-of-dates";
import usePdpModals from "@/hooks/product-detail-page/use-pdp-modals";
import { Box, Slide, Stack } from "@mui/material";
import ProductDetailPageModals from "@/components/product-detail-page/product-detail-page-modals";
import TourHeader from "@/components/product-detail-page/tour-header/tour-header";
import ItinerarySection from "@/components/product-detail-page/itinerary-section/itinerary-section";
import useTourMediaGallery from "@/hooks/product-detail-page/tour-media-gallery/use-tour-media-gallery";
import useTourHeader from "@/hooks/product-detail-page/tour-header/use-tour-header";
import useItinerary from "@/hooks/product-detail-page/itinerary/use-itinerary";
import useViewPrint from "@/hooks/product-detail-page/itinerary/use-view-print";
import useTourSummary from "@/hooks/product-detail-page/tour-summary/use-tour-summary";
import { mockTourInclusions } from "__mocks__/pdp/datesAndPricingMockData";
import TourHeaderButtons from "@/components/product-detail-page/tour-header/tour-header-buttons/tour-header-buttons";
import { MediaCarouselItem, TourHeader as TourHeaderData } from "@/types/dto/pdp/pdp-header-about";
import { TourSummary } from "@/types/dto/pdp/pdp-tour-summary";
import makeUtcDate from "@/utils/date-utils/make-utc-date";
import formatDate from "@/utils/date-utils/format-date";
import PdpDataLayer from "@/components/product-detail-page/data-layer/pdp-data-layer";
import { useMarketCurrency } from "@/providers/market-currency-provider";
import NeedMoreInformationSection from "@/components/forms/need-more-information-section/need-more-information-section";
import { useDateCalculations } from "./dates-and-pricing/use-date-calculations";
import { prefetchPdpComponents } from "./prefetch-orchestrator";
import FeefoReviewsSection from "../common/feefo-reviews-section/feefo-reviews-section";
import ItinerarySummary from "./itinerary-summary/itinerary-summary";
import InclusionsSection from "./inclusions-and-offers/inclusions-section";
import DatesAndPricingSection from "./dates-and-pricing/dates-and-pricing-section";
import TourMediaGallery from "./tour-media-gallery/tour-media-gallery";
import BrandingBar from "./branding-bar/branding-bar";
import Breadcrumb from "../breadcrumb";
import ScrollableStickyNav from "../common/scrollable-sticky-nav";
import LazyLoadAccommodationsSection from "./lazy-load-accommodations/lazy-load-accommodations";
import KeyInformation from "./key-information/key-information";
import LazyLoadFeaturedTourSection from "./lazy-load-featured-tour/lazy-load-featured-tour";
import LazyLoadRelatedBlogsPdpSection from "./lazy-load-related-blogs/lazy-load-related-blogs";
import LazyLoadCrowdRiffGallerySection from "./lazy-load-crowdriff-gallery/lazy-load-crowdriff-gallery";
import ExperienceAddOn from "./experience-add-on/experience-add-on";
import TourOverview from "./tour-summary/tour-overview";
import EnhanceYourTour from "./enhance-your-tour/enhance-your-tour";

interface ProductDetailPageContentProps {
  staticContent: StaticContent;
  dateMetadata: DateMetadata;
  tourHeader?: TourHeaderData;
  initialTourMediaGallery?: MediaCarouselItem[];
  initialTourSummary?: TourSummary;
  }

const ProductDetailPageContent = ({
  staticContent,
  dateMetadata,
  tourHeader,
  initialTourMediaGallery,
  initialTourSummary
}: ProductDetailPageContentProps) => {
  const formatter = useFormatter();
  const t = useTranslations("PDP");
  const locale = useLocale();
  const queryClient = useQueryClient();
  const shareButtonRef = React.useRef<HTMLDivElement>(null);

  // ====================================================================
  // DATE AND PRICING CALCULATIONS
  // ====================================================================

  // Get basic date calculations (tourDates, defaultTourDate, offerDetails)
  const { tourDates, defaultTourDate } = useDateCalculations(dateMetadata);

  // Initialize with default tour date - hydrates correctly from server
  const [selectedTourDate, setSelectedTourDate] = useState<TourDate | null>(defaultTourDate);

  // Get special event calculations that depend on selectedTourDate
  const { specialEventData, selectedDateHasSpecialEvent, offerDetails } = useDateCalculations(
    dateMetadata,
    selectedTourDate
  );

  // Package ID constants derived from selected tour date
  const currentPackageId = selectedTourDate?.packageId || dateMetadata.nextAvailablePackageId;
  const currentPackageDateId =
    selectedTourDate?.packageDateId || dateMetadata.nextAvailablePackageDateId;
  const isInitialDate = currentPackageDateId === dateMetadata.nextAvailablePackageDateId;

  // Get currency/market values from context
  const { currencyId, marketId } = useMarketCurrency();
  // Date prefetching function
  const prefetchDate = (tourDate: TourDate) => {
    const { neoId } = staticContent.tourContent;

    // Only prefetch if we have valid currency and market values
    if (!currencyId || !marketId) {
      return;
    }
    // Use the centralized prefetch orchestrator
    prefetchPdpComponents({
      queryClient,
      tourDate,
      neoId,
      staticContent,
      currencyId,
      marketId
    });
  };

  // ====================================================================
  // STATIC CONTENT AND CONFIGURATION
  // ====================================================================

  // Itinerary summary data for WR-911 (ItinerarySummary component)
  // const itinerarySummaryData = itineraryHeaderFactory(itineraryResponse.itinerary.itineraryHeader);

  const feefoReviewsData = staticContent?.tourContent?.feefoReviewsSection;
  // Use mockCrowdRiffSection if not present in staticContent
  const crowdRiffSection = staticContent?.tourContent?.crowdRiffSection;
  const extensionStylesData = staticContent?.tourContent?.extensionStyles;

  // ====================================================================
  // COMPONENT DATA FETCHING HOOKS
  // ====================================================================

  // Stabilize the neoId and enabled values to prevent hook re-renders
  const neoId = staticContent?.tourContent?.neoId;
  const isEnabled = !isInitialDate && !!currentPackageId;
  const { data: tourHeaderData, isLoading: headerIsLoading } = useTourHeader(
    currentPackageId,
    currentPackageDateId,
    neoId,
    currencyId,
    marketId,
    staticContent as any,
    isEnabled && !!currencyId && !!marketId // Wait for currency and market values
  ); // Use server data for initial load, client data for updates
  const finalTourHeaderData = useMemo(
    () => (isInitialDate && tourHeader ? tourHeader : tourHeaderData),
    [isInitialDate, tourHeader, tourHeaderData]
  );

  const finalTourHeaderDataLoading = useMemo(() => {
    if (isInitialDate) {
      // On initial date, be more conservative - ensure data has key fields for rendering
      return !tourHeader || !tourHeader.tourTitle || !tourHeader.price || !tourHeader.days;
    }
    // On subsequent dates, loading if client hook is loading OR no data ready
    return headerIsLoading || !tourHeaderData || !tourHeaderData.tourTitle;
  }, [isInitialDate, tourHeader, headerIsLoading, tourHeaderData]);

  // Add a small delay to ensure smooth skeleton -> content transition
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    if (!finalTourHeaderDataLoading && finalTourHeaderData) {
      // Small delay to ensure content is ready to render
      const timer = setTimeout(() => setShowContent(true), 100);
      return () => clearTimeout(timer);
    }
    setShowContent(false);
    return undefined;
  }, [finalTourHeaderDataLoading, finalTourHeaderData]);

  // Override loading state to include content readiness
  const finalTourHeaderDataLoadingWithDelay = finalTourHeaderDataLoading || !showContent;

  // Fetch full itinerary data for WR-912 (ItinerarySection component)
  // Use dynamic IDs from API if available, otherwise fall back to mock IDs
  const { data: itineraryData, isLoading: itineraryIsLoading } = useItinerary(
    currentPackageId || "16128",
    currentPackageDateId || "107496",
    staticContent?.tourContent?.neoId || "686",
    staticContent?.tourContent?.landingPageId,
    currencyId,
    marketId,
    !!currencyId && !!marketId // Only enable when both currency and market are available
  );
  // ====================================================================
  // TOUR MEDIA GALLERY LOGIC
  // ====================================================================

  const { data: tourGalleryData, isLoading: mediaGalleryLoading } = useTourMediaGallery(
    currentPackageId,
    currentPackageDateId,
    staticContent?.tourContent?.neoId,
    undefined, // contentfulData not used for media gallery
    !isInitialDate && !!currentPackageId // Only enabled when not initial date
  );

  // Use server data for initial load, client data for updates
  const finalTourMediaGallery = useMemo(
    () => (isInitialDate && initialTourMediaGallery ? initialTourMediaGallery : tourGalleryData),
    [isInitialDate, initialTourMediaGallery, tourGalleryData]
  );

  const finalMediaGalleryLoading = useMemo(
    () => (isInitialDate ? !initialTourMediaGallery : mediaGalleryLoading),
    [isInitialDate, initialTourMediaGallery, mediaGalleryLoading]
  );

  // Note: Manual image preloading removed - Next.js Image with priority={true} handles this automatically

  // ====================================================================
  // UI STATE AND INTERSECTION OBSERVER
  // ====================================================================

  const [showPdpButtons, setShowPdpButtons] = useState<boolean>(false);

  // use Effect to transition header buttons in either sticky nav (d) or bottom drawer (m/t) JF
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setShowPdpButtons(!entry.isIntersecting);
        });
      },
      {
        root: null,
        threshold: 0,
        rootMargin: "-100px"
      }
    );

    const cleanupRef = document.getElementById("sticky-intersection-observer-element");
    if (cleanupRef) {
      observer.observe(cleanupRef);
    }
    return () => {
      if (cleanupRef) {
        observer.unobserve(cleanupRef);
      }
    };
  }, []);

  // ====================================================================
  // MODAL STATE MANAGEMENT
  // ====================================================================

  // Handler to scroll to reviews section
  const scrollToReviews = () => {
    const reviewsSection = document.getElementById("feefo-reviews");
    if (reviewsSection) {
      reviewsSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const {
    activityLevelIsOpen,
    defaultActivityLevel,
    handleOpenActivityLevel,
    handleCloseActivityLevel,
    travelStylesIsOpen,
    handleOpenTravelStyles,
    handleCloseTravelStyles,
    onTourAirIsOpen,
    handleOpenOnTourAir,
    handleCloseOnTourAir,
    requestAQuoteIsOpen,
    handleCloseRequestAQuote,
    handleOpenRequestAQuote,
    printIsOpen,
    handleOpenPrint,
    handleClosePrint,
    printStatus,
    setPrintStatus,
    shareIsOpen,
    handleOpenShare,
    handleCloseShare,
    bestTimeToTravelIsOpen,
    handleOpenBestTimeToTravel,
    handleCloseBestTimeToTravel,
    offerTermsIsOpen,
    handleOpenOfferTerms,
    handleCloseOfferTerms,
    extensionStylesIsOpen,
    handleOpenExtensionStyles,
    handleCloseExtensionStyles,
    currentExtensionStyle
  } = usePdpModals();

  const { handleViewPrint, handleClosePrint: handleCloseViewPrint, handleOpenReady, canViewPrint } = useViewPrint({
    selectedTourDate,
    locale,
    marketId,
    openModal: handleOpenPrint,
    closeModal: handleClosePrint,
    setStatus: setPrintStatus
  });

  const { data: tourSummaryData, isLoading: tourSummaryIsLoading } = useTourSummary(
    currentPackageId,
    currentPackageDateId,
    neoId,
    currencyId,
    marketId,
    staticContent as any, // contentfulData
    isEnabled && !!currencyId && !!marketId
  );

  // Use server data for initial load, client data for updates
  const finalTourSummaryData = useMemo(
    () => (isInitialDate && initialTourSummary ? initialTourSummary : tourSummaryData),
    [isInitialDate, initialTourSummary, tourSummaryData]
  );

  const finalTourSummaryDataLoading = useMemo(
    () => (isInitialDate ? !initialTourSummary : tourSummaryIsLoading),
    [isInitialDate, initialTourSummary, tourSummaryIsLoading]
  );
  const phoneNumber = staticContent?.modalContent?.contactFormSection?.phoneNumber;
    
  return (
    <Stack
      className="destination-page-container"
      id="product-detail-page-content"
      sx={{
        padding: {
          legacyDesktop: "1.5rem 7rem",
          legacyMobile: "2.4rem",
          legacyTablet: "4.0 rem 3.2rem clamp(2.4rem, 4.5vw, 6.5rem)"
        },
        overflow: "visible"
      }}
      data-page-group-type="Product Page"
    >
      <Box id="sticky-intersection-observer-element">
        <Breadcrumb title={staticContent?.tourContent?.tourTitle} />
        <TourHeader
          tourTitle={staticContent?.tourContent?.tourTitle}
          tourHeaderData={finalTourHeaderData}
          handleOpenActivityLevel={() =>
            handleOpenActivityLevel(finalTourHeaderData?.activityLevelNumber ?? 0)
          }
          handleOpenTravelStyles={handleOpenTravelStyles}
          handleOpenOnTourAir={handleOpenOnTourAir}
          handleOpenRequestAQuote={handleOpenRequestAQuote}
          scrollToReviews={scrollToReviews}
          isLoading={finalTourHeaderDataLoadingWithDelay}
        />
        <TourMediaGallery
          tourGalleryData={finalTourMediaGallery}
          isLoading={finalMediaGalleryLoading}
        />
      </Box>
      <ScrollableStickyNav
        navItems={[
          { label: t("Tour Highlights"), anchor: "tour-overview" },
          { label: t("Itinerary"), anchor: "itinerary" },
          { label: t("Accommodations"), anchor: "accommodations" },
          { label: t("Essential Tour Information"), anchor: "essential-tour-information" },
          ...(feefoReviewsData?.feefoReviews !== undefined && feefoReviewsData.feefoReviews.length > 0
            ? [{ label: t("Reviews"), anchor: "feefo-reviews" }]
            : [])
        ]}
        pdpOptions={{
          showPdpButtons,
          handleOpenRaq: handleOpenRequestAQuote
        }}
        moduleRankingPosition="4"
      />

      {/* Tour Overview Section */}
      <TourOverview
        tourSummaryData={finalTourSummaryData}
        phoneNumber={phoneNumber}
        isLoading={finalTourSummaryDataLoading}
        activityLevels={staticContent?.modalContent?.activityLevels ?? []}
        travelStyles={staticContent?.modalContent?.travelStyles ?? []}
      />

      <Stack
        sx={{
          padding: {
            lg: "0 3rem 4rem",
            md: "4.0 rem 3.2rem clamp(2.4rem, 4.5vw, 6.5rem)"
          },
          overflow: "visible"
        }}
      >
        {/* Branding Section */}
        <BrandingBar bannerData={staticContent.tourContent.banner} />
        {/* Itinerary Summary - WR-911 (Summary bar) */}
        <ItinerarySummary
          itineraryHeader={itineraryData?.itineraryHeader}
          handleOpenShare={handleOpenShare}
          handleOpenPrint={handleViewPrint}
          canViewPrint={canViewPrint}
          handleOpenBestTimeToTravelIsOpen={handleOpenBestTimeToTravel}
          hasBestTimeToTravel={!!staticContent?.modalContent?.bestTimeToTravel?.headerText}
          isLoading={itineraryIsLoading}
          shareButtonRef={shareButtonRef}
        />
        {/* Itinerary Section - WR-912 (Day-by-day details) */}
        <ItinerarySection
          itinerary={itineraryData}
          testIdPrefix="pdp"
          handleOpenExtensionStyles={handleOpenExtensionStyles}
          extensionStyleProps={extensionStylesData ?? []}
          isLoading={itineraryIsLoading}
          handleOpenActivityLevel={handleOpenActivityLevel}
        />
        {/* Module 9: Inclusions & Offers and Dates & Pricing - WR-913 */}
        <Box
          className="fluid-box"
          sx={{
            backgroundColor: "background.warm_strong",
            display: "flex",
            justifyContent: "center"
          }}
        >
          <Box
            className="destination-page-container"
            sx={{
              padding: {
                zero: "4rem 2.4rem",
                legacyMobile: "4rem 2.4rem",
                legacyTablet: "4rem clamp(2.4rem, 4.5vw, 6.5rem)"
              },
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              gap: "4rem",
              justifyContent: { xs: "center", lg: "space-between" },
              width: "100%",
              maxWidth: "1440px"
            }}
          >
            <InclusionsSection
              inclusions={mockTourInclusions}
              offerDetails={offerDetails}
              specialEventData={specialEventData}
              showSpecialEvent={selectedDateHasSpecialEvent}
              onRequestQuote={handleOpenRequestAQuote}
              onViewOfferDetails={handleOpenOfferTerms}
              handleOpenOnTourAir={handleOpenOnTourAir}
              hasOnTourAir={tourHeaderData?.onTourAir ?? false}
              hasReverseItinerary={selectedTourDate?.itineraryType === "Reverse"}
            />
            <DatesAndPricingSection
              tourDates={tourDates}
              selectedDateId={selectedTourDate?.packageDateId ?? null}
              onRequestQuote={handleOpenRequestAQuote}
              onViewItinerary={(tourDate) => {
                setSelectedTourDate(tourDate);
                // Scroll to itinerary summary (above the day-by-day section)
                const itinerarySummary = document.getElementById("itinerary");
                itinerarySummary?.scrollIntoView({ behavior: "smooth" });
              }}
              onBookNow={(tourDate) => {
                window.open(tourDate.bookNowUrl, "_blank");
              }}
              onDateSelect={setSelectedTourDate}
              onDateHover={prefetchDate}
              hasOnTourAir={tourHeaderData?.onTourAir ?? false}
              customerCareNumber={phoneNumber}
            />
          </Box>
        </Box>

        <LazyLoadAccommodationsSection
          packageId={currentPackageId}
          packageDateId={currentPackageDateId}
          neoId={staticContent.tourContent.neoId}
        />

        {itineraryData && (itineraryData.preTour?.price || itineraryData.postTour?.price) &&  (
        <EnhanceYourTour
          preTour={itineraryData?.preTour}
          postTour={itineraryData?.postTour}
          isLoading={itineraryIsLoading}
          handleOpenActivityLevel={handleOpenActivityLevel}
          handleOpenExtensionStyles={handleOpenExtensionStyles}
          extensionStyleProps={extensionStylesData ?? []}
        />
        )}

        {(itineraryData?.excursions?.length ?? 0) > 0 && (
        <ExperienceAddOn
          dayUpgrades={itineraryData?.excursions ?? []}
          isLoading={itineraryIsLoading}
        />
        )}
        <NeedMoreInformationSection
          moduleRankingPosition="14"
          contactFormSection={staticContent?.modalContent?.contactFormSection}
          tourId={staticContent.tourContent.landingPageId}
          tourTitle={`${staticContent.tourContent.tourTitle} ${staticContent.tourContent.tourSubtitle}`}
        />
        <KeyInformation
          packageId={currentPackageId}
          neoId={staticContent?.tourContent?.neoId ?? ""}
          keyInfoHeader={staticContent?.tourContent?.keyInformationHeader}
        />
        {feefoReviewsData?.feefoReviews !== undefined && feefoReviewsData.feefoReviews.length > 0 && (
          <FeefoReviewsSection
            section={feefoReviewsData}
            moduleRankingPosition="16"
            isProductClickable={false}
          />
        )}
        {crowdRiffSection && (
          <LazyLoadCrowdRiffGallerySection
            crowdRiffSection={crowdRiffSection}
            showCopyText={false}
            moduleRankingPosition="17"
          />
        )}
        {staticContent?.tourContent?.landingPageId && (
          <LazyLoadRelatedBlogsPdpSection
            landingPageId={staticContent?.tourContent?.landingPageId}
          />
        )}
        {staticContent?.tourContent?.landingPageId && (
          <LazyLoadFeaturedTourSection
            tourCarousel={{
              currencyId,
              marketId,
              landingPageId: staticContent?.tourContent?.landingPageId,
              featureCardDetails: staticContent?.tourContent?.featuredTourDetails
            }}
          />
        )}
        <Slide direction="up" in={showPdpButtons} mountOnEnter unmountOnExit>
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 800,
              display: { xs: "flex", lg: "none" },
              padding: "1.2rem",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "background.default",
              boxShadow: 6
            }}
          >
            <TourHeaderButtons
              handleOpenRequestAQuote={handleOpenRequestAQuote}
              variant="bottom-drawer"
              testId="bottom-drawer"
            />
          </Box>
        </Slide>
        <ProductDetailPageModals
          activityLevelIsOpen={activityLevelIsOpen}
          handleCloseActivityLevel={handleCloseActivityLevel}
          travelStylesIsOpen={travelStylesIsOpen}
          handleCloseTravelStyles={handleCloseTravelStyles}
          onTourAirIsOpen={onTourAirIsOpen}
          handleCloseOnTourAir={handleCloseOnTourAir}
          requestAQuoteIsOpen={requestAQuoteIsOpen}
          handleCloseRequestAQuote={handleCloseRequestAQuote}
          shareButtonRef={shareButtonRef}
          requestAQuoteProps={{
            tourId: finalTourHeaderData?.tourId ?? "",
            title: finalTourHeaderData?.tourTitle ?? "",
            subtitle: finalTourHeaderData?.tourSubtitle,
            tourDates: formatListOfDates(dateMetadata?.departureDates, formatter) ?? [],
            landingPageId: staticContent?.tourContent.landingPageId ?? "",
            selectedDate: formatDate({
              utcDate: makeUtcDate(selectedTourDate?.startDate ?? "") ?? new Date(),
              formatter,
              monthFormat: "2-digit",
              dayFormat: "2-digit",
              yearFormat: "numeric"
            })
          }}
          travelStylesProps={{
            travelStyles: staticContent?.modalContent.travelStyles ?? [],
            tourTravelStyles: finalTourHeaderData?.travelStyles ?? []
          }}
          activityLevelProps={{
            activityLevelNumber: finalTourHeaderData?.activityLevelNumber ?? 0,
            openTo: defaultActivityLevel ?? finalTourHeaderData?.activityLevelNumber ?? 0,
            activityLevelDetails: staticContent?.modalContent.activityLevels ?? []
          }}
          onTourAir={staticContent?.modalContent?.onTourAir}
          printIsOpen={printIsOpen}
          handleClosePrint={handleCloseViewPrint}
          printProps={{
            tourName: finalTourHeaderData?.tourTitle ?? staticContent?.tourContent?.tourTitle ?? "",
            dateRange: selectedTourDate?.tourStartTourEnd ?? "",
            status: printStatus,
            handleOpen: handleOpenReady
          }}
          shareIsOpen={shareIsOpen}
          handleCloseShare={handleCloseShare}
          bestTimeToTravelIsOpen={bestTimeToTravelIsOpen}
          handleCloseBestTimeToTravel={handleCloseBestTimeToTravel}
          bestTimeToTravelProps={staticContent?.modalContent?.bestTimeToTravel}
          offerTermsIsOpen={offerTermsIsOpen}
          handleCloseOfferTerms={handleCloseOfferTerms}
          offerTermsProps={{
            offerTitle: offerDetails?.offerTitle ?? "",
            termsAndConditions: offerDetails?.termsAndConditions ?? ""
          }}
          extensionStylesIsOpen={extensionStylesIsOpen}
          handleCloseExtensionStyles={handleCloseExtensionStyles}
          extensionStyleProps={extensionStylesData}
          currentExtensionStyle={currentExtensionStyle}
        />
        {staticContent && finalTourHeaderData && selectedTourDate && (
          <PdpDataLayer
            staticData={staticContent}
            tourHeaderData={finalTourHeaderData}
            selectedDate={selectedTourDate}
          />
        )}
      </Stack>
    </Stack>
  );
};

export default ProductDetailPageContent;
